import { Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { HostListenerService } from '../host-listener.service';

/** A single recorded user action, kept in the rolling breadcrumb buffer. */
export interface Breadcrumb {
    type: 'navigate' | 'click';
    /** Human-readable description of the action. */
    label: string;
    /** The URL the user was on when the action happened. */
    url: string;
    /** Epoch millis the action was recorded. */
    ts: number;
}

/**
 * Always-on rolling buffer of the user's recent navigations and clicks, used to
 * pre-fill the "How you got here" repro-steps field on a bug report. Subscribes
 * to Router `NavigationEnd` events and the global document-click bus
 * (`HostListenerService.documentClickedEvent$`).
 *
 * The click subscription runs inside `NgZone.runOutsideAngular` so capturing a
 * breadcrumb never triggers change detection — this is a passive recorder and
 * must not cost a CD cycle per click. It bootstraps via an `APP_INITIALIZER`
 * (see app.module.ts) so the buffer starts filling the moment the app loads.
 */
@Injectable({ providedIn: 'root' })
export class BreadcrumbBufferService {
    /** Maximum breadcrumbs retained; oldest are evicted (ring buffer). */
    public static readonly MAX_ENTRIES = 50;

    private _buffer: Breadcrumb[] = [];
    private _started = false;

    constructor(
        private _router: Router,
        private _zone: NgZone,
        private _hostListener: HostListenerService,
    ) {}

    /**
     * Begin recording. Idempotent — calling more than once is a no-op so the
     * APP_INITIALIZER can't double-subscribe. Listeners run outside Angular's
     * zone; recording never calls markForCheck.
     */
    public start(): void {
        if (this._started) {
            return;
        }
        this._started = true;

        this._zone.runOutsideAngular(() => {
            this._router.events
                .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
                .subscribe((event) => {
                    this._record({
                        type: 'navigate',
                        label: `Navigated to ${event.urlAfterRedirects}`,
                        url: event.urlAfterRedirects,
                        ts: Date.now(),
                    });
                });

            this._hostListener.documentClickedEvent$.subscribe((event) => {
                this._record({
                    type: 'click',
                    label: `Clicked ${this._describeTarget(event)}`,
                    url: this._router.url,
                    ts: Date.now(),
                });
            });
        });
    }

    /** Append a breadcrumb, evicting the oldest entry once full. */
    private _record(entry: Breadcrumb): void {
        this._buffer.push(entry);
        if (this._buffer.length > BreadcrumbBufferService.MAX_ENTRIES) {
            this._buffer.shift();
        }
    }

    /**
     * Best-effort human-readable description of a clicked element: its visible
     * text (trimmed), else an aria-label, else the tag name.
     */
    private _describeTarget(event: MouseEvent): string {
        const target = event.target as HTMLElement | null;
        if (!target) {
            return 'an element';
        }
        const text = (target.textContent ?? '').trim().replace(/\s+/g, ' ');
        if (text.length > 0) {
            return `"${text.slice(0, 40)}"`;
        }
        const ariaLabel = target.getAttribute?.('aria-label');
        if (ariaLabel) {
            return `"${ariaLabel.slice(0, 40)}"`;
        }
        return target.tagName ? `<${target.tagName.toLowerCase()}>` : 'an element';
    }

    /** Defensive copy of the raw breadcrumbs (newest last). */
    public getBreadcrumbs(): Breadcrumb[] {
        return [...this._buffer];
    }

    /**
     * Human-readable steps for the repro field — one line per breadcrumb,
     * oldest first. Suitable as the default value of the editable repro-steps
     * input on the bug-report drawer.
     */
    public getSteps(): string[] {
        return this._buffer.map((b) => b.label);
    }

    /** Empty the buffer — called after a bug report is successfully submitted. */
    public clear(): void {
        this._buffer = [];
    }
}
